import { z } from "zod";

export const RESUME_SCHEMA_VERSION = 1;

export const defaultLayout = [
  [
    ["profiles", "summary", "experience", "education", "projects", "volunteer", "references"],
    ["skills", "interests", "certifications", "awards", "publications", "languages"],
  ],
];

// Schema
export const metadataSchema = z.object({
  schemaVersion: z.number().int().default(RESUME_SCHEMA_VERSION),
  template: z.string().default("rhyhorn"),
  layout: z.array(z.array(z.array(z.string()))).default(defaultLayout), // pages -> columns -> sections
  css: z.object({
    value: z.string().default("* {\n\toutline: 1px solid #000;\n\toutline-offset: 4px;\n}"),
    visible: z.boolean().default(false),
  }),
  page: z.object({
    margin: z.number().default(18),
    format: z.enum(["a4", "letter"]).default("a4"),
    options: z.object({
      breakLine: z.boolean().default(true),
      pageNumbers: z.boolean().default(true),
    }),
  }),
  theme: z.object({
    background: z.string().default("#ffffff"),
    text: z.string().default("#000000"),
    primary: z.string().default("#dc2626"),
  }),
  typography: z.object({
    font: z.object({
      family: z.string().default("IBM Plex Serif"),
      subset: z.string().default("latin"),
      variants: z.array(z.string()).default(["regular"]),
      size: z.number().default(14),
    }),
    lineHeight: z.number().default(1.5),
    hideIcons: z.boolean().default(false),
    underlineLinks: z.boolean().default(true),
  }),
  // Spec 046 US3 — FlowCV-depth Customize.
  // All OPTIONAL with no defaults on purpose: an unset field means "use the template's
  // native styling", so pre-existing resumes are visually unchanged until the user opts in
  // (FR-014). The Customize panels show a sensible fallback for display and only write a
  // value when the user changes the control.
  spacing: z
    .object({
      section: z.number(), // px gap between sections
      entry: z.number(), // px gap between entries within a section
    })
    .partial()
    .optional(),
  header: z
    .object({
      align: z.enum(["left", "center"]),
      iconStyle: z.enum([
        "no-frame",
        "circle-filled",
        "rounded-filled",
        "square-filled",
        "circle-outline",
        "rounded-outline",
      ]),
    })
    .partial()
    .optional(),
  entry: z
    .object({
      separator: z.enum(["normal", "bullet", "hyphen"]),
      dateWidth: z.enum(["auto", "manual"]),
    })
    .partial()
    .optional(),
  headings: z
    .object({
      case: z.enum(["none", "upper"]),
      underline: z.boolean(),
      weight: z.enum(["regular", "medium", "semibold", "bold"]),
    })
    .partial()
    .optional(),
  links: z
    .object({
      style: z.enum(["text", "underline", "icon"]),
    })
    .partial()
    .optional(),
  dateFormat: z.enum(["MMM YYYY", "MM/YYYY", "YYYY", "MMMM YYYY", "MMM 'YY"]).optional(),
  language: z.string().optional(),
  layoutMode: z.enum(["one", "two", "mix"]).optional(), // columns intent; the layout array stays the source of truth for section placement
  footer: z
    .object({
      customText: z.string(),
    })
    .partial()
    .optional(),
  notes: z.string().default(""),
});

// Type
export type Metadata = z.infer<typeof metadataSchema>;

// Defaults
export const defaultMetadata: Metadata = {
  schemaVersion: RESUME_SCHEMA_VERSION,
  template: "rhyhorn",
  layout: defaultLayout,
  css: {
    value: "* {\n\toutline: 1px solid #000;\n\toutline-offset: 4px;\n}",
    visible: false,
  },
  page: {
    margin: 18,
    format: "a4",
    options: {
      breakLine: true,
      pageNumbers: true,
    },
  },
  theme: {
    background: "#ffffff",
    text: "#000000",
    primary: "#dc2626",
  },
  typography: {
    font: {
      family: "IBM Plex Serif",
      subset: "latin",
      variants: ["regular", "italic", "600"],
      size: 14,
    },
    lineHeight: 1.5,
    hideIcons: false,
    underlineLinks: true,
  },
  notes: "",
};
